import { useCallback, useRef, useState } from 'react';

import { useDisclosure } from '@chakra-ui/react';

import { useDebounceCallback } from '@enterprise/hooks';

import type { UseConfirmationDialogOptions } from '#modals/types/use-confirmation-dialog.options';

/**
 * Custom hook for managing a confirmation dialog with debounced confirmation input.
 *
 * @param confirmationText - The text required to confirm the dialog action.
 * @param defaultIsOpen - Optional flag to set the dialog's initial open state.
 * @returns An object containing:
 *   - `cancelRef`: Ref for the cancel button element.
 *   - `confirmed`: Boolean indicating if the confirmation text matches.
 *   - `handleConfirmationChange`: Debounced callback to handle confirmation input changes.
 *   - `isOpen`: Boolean indicating if the dialog is open.
 *   - `onClose`: Function to close the dialog and reset confirmation state.
 *   - `onOpen`: Function to open the dialog.
 */
export const useConfirmationDialogConfiguration = ({
  confirmationText,
  defaultIsOpen = false,
}: UseConfirmationDialogOptions) => {
  const [confirmed, setConfirmed] = useState(false);

  const { isOpen, onClose, onOpen } = useDisclosure({ defaultIsOpen });
  const cancelRef = useRef<HTMLButtonElement>(null);

  const handleConfirmationChange = useDebounceCallback((value: string) => {
    setConfirmed(value === confirmationText);
  }, 500);

  const handleClose = useCallback(() => {
    setConfirmed(false);
    onClose();
  }, [onClose]);

  return { cancelRef, confirmed, handleConfirmationChange, isOpen, onClose: handleClose, onOpen };
};
